// "Rapor aktarımı" alanı — Geçmiş Rapor Aktarımı (Task 14, tasarım review 3A).
//
// 3A: `ImportResultBand` bileşeni İPTAL — tek `ImportSummarySection`, bölücülü 3 satır:
//   1) kaynak satırı (`bandImported`: dosya + sayılar) + "raporu aç" + `fallbackNote`,
//   2) sonuç detayı (`formatResultLines`) — geri çekilmişse `archivedBand` önce görünür
//      ve bu satır `opacity:.6` olur,
//   3) eşlenmemiş içerik (`unmappedBandTitle`/`Hint` + "göster" → `UnmappedDrawer`,
//      n=0 iken gizli).
//
// `provenance.imports[]` yalnız `ImportSummary` taşır (sayaç YOK) — en son import
// (`pickLatestImport`) seçildikten sonra `GET imports/:id` ile ayrıca detay (`stats`,
// `unmappedCount`) çekilir (T1 `ImportSummarySchema`, api-route-table.md).

import { useEffect, useState } from "react";
import type { ImportSummary, ProjectProvenanceResponse, ReportImportDetail } from "@mahir/shared";
import { getImport } from "../../api/report-import";
import { fillTemplate } from "../../lib/period-ui";
import {
  IMPORT_COPY,
  fileOpenUrl,
  formatResultLines,
  possessiveSuffixTr,
  totalQuestionsFromStats,
  writtenAnswersFromStats,
  pickLatestImport,
} from "../../lib/report-import-ui";
import { UnmappedDrawer } from "../report-import/UnmappedDrawer";

export interface ImportSummarySectionProps {
  /** `null` — henüz yüklenmedi (loading) ya da hiç import yok; her iki durumda da
   * (bir sonraki adımda `imports.length===0` kontrolü) görsel gürültü çıkarmaz. */
  provenance: ProjectProvenanceResponse | null;
  /** Köken fetch'i BAŞARISIZ oldu (null'dan farklı — 8A). */
  provenanceError: boolean;
  /** "raporu aç" / "Raporda aç" linkleri yalnız `projects.import_report` izniyle görünür. */
  canOpenReport: boolean;
}

function archivedDateText(
  createSummary: ImportSummary | null,
  detail: ReportImportDetail | null,
): string {
  const raw =
    createSummary?.finishedAt ??
    (detail && detail.id === createSummary?.id ? detail.updatedAt : null);
  if (!raw) return "—";
  return new Date(raw).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function ImportSummarySection(props: ImportSummarySectionProps): JSX.Element | null {
  const { provenance, provenanceError, canOpenReport } = props;
  const [openImportId, setOpenImportId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReportImportDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const latest = provenance ? pickLatestImport(provenance.imports) : null;
  const createSummary = provenance?.imports.find((i) => i.mode === "create") ?? null;
  const latestId = latest?.id ?? null;
  const latestStatus = latest?.status ?? null;

  // `latestStatus` de bağımlılıkta — ProjectDetail'in 5 sn poll'ü `provenance`'ı
  // güncellediğinde `latestId` AYNI kalır (hâlâ en son import) ama `status`
  // (queued → … → done) değişir; bu olmadan `detail.stats`/`unmappedCount` sayfa
  // yenilemeden asla tazelenmezdi (Fix round 1 — canlı provada yakalandı).
  useEffect(() => {
    if (!latestId) {
      setDetail(null);
      return;
    }
    let aborted = false;
    setDetailLoading(true);
    getImport(latestId)
      .then((res) => {
        if (!aborted) setDetail(res.import);
      })
      .catch(() => {
        if (!aborted) setDetail(null);
      })
      .finally(() => {
        if (!aborted) setDetailLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [latestId, latestStatus]);

  if (provenanceError) {
    return (
      <div className="mt-4 pt-3" style={{ borderTop: "1px solid var(--color-border)" }}>
        <span style={{ fontSize: "0.78rem", color: "var(--color-text-2)" }}>
          {IMPORT_COPY.provenanceUnavailable}
        </span>
      </div>
    );
  }

  if (!provenance || !latest) return null;

  const isArchived = createSummary?.status === "archived";
  const multiImports = provenance.imports.length > 1;

  return (
    <div className="mt-4 pt-3" style={{ borderTop: "1px solid var(--color-border)" }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-2)" }}>
        Rapor aktarımı
        {multiImports
          ? ` · ${fillTemplate(IMPORT_COPY.importCountSuffix, { n: provenance.imports.length })}`
          : ""}
      </div>

      {detailLoading && !detail ? (
        <div className="mt-2" style={{ fontSize: "0.78rem", color: "var(--color-text-2)" }}>
          {IMPORT_COPY.loadingGeneric}
        </div>
      ) : detail ? (
        <>
          {/* Satır 1 — kaynak: dosya + sayılar + raporu aç + fallbackNote. */}
          <div className="mt-2" style={{ fontSize: "0.875rem", color: "var(--color-text)" }}>
            {fillTemplate(IMPORT_COPY.bandImported, {
              file: latest.fileName,
              // run3 K1 — pay = yazılan yanıt (düşük güven dahil); FormFill bandıyla aynı.
              answered: writtenAnswersFromStats(detail.stats),
              total: totalQuestionsFromStats(detail.stats),
              low: detail.stats?.lowConfidence ?? 0,
              // S6 — Türkçe iyelik eki sayıdan türer (3 → "3'ü", 7 → "7'si").
              lowSuffix: possessiveSuffixTr(detail.stats?.lowConfidence ?? 0),
              rejected:
                (detail.stats?.rejected.grounding ?? 0) + (detail.stats?.rejected.schema ?? 0),
            })}{" "}
            {canOpenReport ? (
              <a
                href={fileOpenUrl(latest.fileUrl, latest.fileKind)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "var(--color-primary)",
                  textDecoration: "underline",
                  fontWeight: 600,
                }}
              >
                {IMPORT_COPY.bandOpenReport}
              </a>
            ) : null}
          </div>
          {detail.stats?.fallbackToPdf ? (
            <div style={{ fontSize: "0.75rem", color: "var(--color-text-2)", marginTop: 2 }}>
              {IMPORT_COPY.fallbackNote}
            </div>
          ) : null}

          {/* Satır 2 — sonuç detayı; geri çekildiyse önce nötr bant, sonra bu satır sönük. */}
          {isArchived ? (
            <div
              className="mt-2"
              style={{
                fontSize: "0.78rem",
                color: "var(--color-text-2)",
                background: "var(--color-bg)",
                border: "1px solid var(--color-border)",
                borderRadius: 6,
                padding: "4px 8px",
                display: "inline-block",
              }}
            >
              {fillTemplate(IMPORT_COPY.archivedBand, {
                date: archivedDateText(createSummary, detail),
              })}
            </div>
          ) : null}
          {(() => {
            const total = totalQuestionsFromStats(detail.stats);
            const lines = detail.stats ? formatResultLines(detail.stats, total) : null;
            if (!lines) return null;
            return (
              <div className="mt-1.5" style={{ opacity: isArchived ? 0.6 : 1 }}>
                <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text-2)" }}>
                  {lines.primary}
                </div>
                {lines.exceptions ? (
                  <div style={{ fontSize: "0.78rem", color: "var(--color-warning-text)" }}>
                    {lines.exceptions}
                  </div>
                ) : null}
              </div>
            );
          })()}

          {/* Satır 3 — eşlenmemiş içerik, n=0 iken gizli. */}
          {detail.unmappedCount > 0 ? (
            <div
              className="mt-2"
              style={{
                fontSize: "0.85rem",
                background: "var(--color-warning-bg)",
                border: "1px solid var(--color-warning-border)",
                borderRadius: 8,
                padding: "6px 10px",
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontWeight: 600, color: "var(--color-warning-text)" }}>
                {fillTemplate(IMPORT_COPY.unmappedBandTitle, { n: detail.unmappedCount })}
              </span>
              <span style={{ fontSize: "0.78rem", color: "var(--color-warning-text)" }}>
                {IMPORT_COPY.unmappedBandHint}
              </span>
              <button
                type="button"
                onClick={() => setOpenImportId(detail.id)}
                style={{
                  border: "none",
                  background: "none",
                  color: "var(--color-warning-text)",
                  textDecoration: "underline",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                {IMPORT_COPY.unmappedShow}
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      <UnmappedDrawer
        importId={openImportId}
        onClose={() => setOpenImportId(null)}
        canOpenReport={canOpenReport}
      />
    </div>
  );
}
